import { Link, useOutletContext } from "react-router-dom";

import PageAbstract from "components/PageAbstract";
import socialAccounts from "socialAccounts";
import _request from "actions/_request";
import _helper from "actions/_helper";
import i18n from "i18";
import { isAppleBilled, ACCOUNT_LIMIT_REDIRECT_CODE } from "utils/billing";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import "./style.css";
import React from "react";
import AvatarIcon from "../../components/AvatarIcon";
import IconsAccounts from "../../components/IconsAccounts";
import { ReactComponent as EmptyAcc } from "../../images/No accounts.svg";
import { AddIcon, MoreHorizontalIcon } from "../../components/AdaptiveIcon";
import TelegramChatSelector from "../../components/TelegramChatSelector";
import AccountAnalytics from "../../components/AccountAnalytics";
import GroupActionsMenu from "./GroupActionsMenu";

class AccountsPage extends PageAbstract {
  constructor(props) {
    super(props);

    this.state = {
      ...this.state,
      items: [],
      users: [],
      groups: [],
      userTeam: [],
      metaData: {},
      selectedAccounts: [],
      profileMenuOpened: null,
      deleteMenuOpened: null,
      deleteGroupOpened: null,
      deleteGroupTarget: null,
      dialogOpen: false,
      deleteDialogOpen: false,
      accountToDelete: null,
      deleteWarningOpen: false,
      errorDialogOpen: false,
      errorMessage: "",
      errorTitle: "",
      callbackError: null,
      showCallbackBanner: false,
      callbackSuccess: null,
      showSuccessBanner: false,
    };

    this.setProfileMenuOpened = this.setProfileMenuOpened.bind(this);
    this.setDeleteMenuOpened = this.setDeleteMenuOpened.bind(this);
    this.setDeleteGroupOpened = this.setDeleteGroupOpened.bind(this);
    this.handleClickOpen = this.handleClickOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  async loadAcc() {
    const { companyType } = this.props;
    await _request.accounts.loading(async () => {
      const accResp = await _request.accounts.getAll([], [], companyType);
      const items = accResp?.data?.items || [];

      const groupsResp = await _request.groups.getAll();
      const groups = groupsResp?.data?.items || [];

      const teamResp = await _request.userTeam.getAll();
      const userTeam = teamResp?.data?.items || [];

      this.setState({ items, groups, userTeam });
    });
  }

  async loadData() {
    const resp = await _request.accounts.getAll();
    const items = resp?.data?.items || [];

    const accResp = await _request.users.getAll();
    const users = accResp?.data?.items || [];

    const groupsResp = await _request.groups.getAll();
    const groups = groupsResp?.data?.items || [];

    const res = await _request.users.getMeta();
    const metaData = res?.data || {};

    this.loadAcc();

    this.setState({ items, users, groups, metaData });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.companyType !== this.props.companyType) {
      this.loadAcc();
    }
  }

  componentDidMount() {
    const hasCallbackError = this.checkCallbackError();
    this.checkLinkedinReconnect();
    if (!hasCallbackError) {
      super.componentDidMount();
    }
  }

  checkLinkedinReconnect() {
    const params = new URLSearchParams(window.location.search);
    const value = params.get("reconnect_linkedin");
    if (!value) return;

    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);

    if (value === "profile" || value === "page") {
      this.addAccountSend({ social_type: "linkedin", account_type: value });
    } else {
      this.openLinkedInModeDialog();
    }
  }

  checkCallbackError() {
    if (window.location.hash === "#_=_") {
      const cleanUrl = window.location.pathname + window.location.search;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    const urlParams = new URLSearchParams(window.location.search);
    const errorCode = urlParams.get("error_code");
    const provider = urlParams.get("provider");
    const detail = urlParams.get("detail");
    const success = urlParams.get("success");
    const added = urlParams.get("added");
    const warnings = urlParams.get("warnings");

    // Legacy params (older backend builds may still send these)
    const legacyError = urlParams.get("error");
    const legacyDescription = urlParams.get("error_description");
    const legacyCode = urlParams.get("code");

    const hasCallbackError = !!errorCode || !!legacyError || !!legacyCode;
    const hasCallbackNotice = hasCallbackError || !!success;

    if (errorCode) {
      this.handleCallbackError(errorCode, provider, detail);
    } else if (success) {
      this.handleCallbackSuccess(added, warnings, provider);
    } else if (legacyError) {
      this.handleLegacyCallbackError(legacyError, legacyDescription);
    } else if (legacyCode) {
      this.handleLegacyCallbackError(
        "callback_failed",
        "Failed to process social network response.",
      );
    }

    if (hasCallbackNotice) {
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, document.title, cleanUrl);
    }

    return hasCallbackError;
  }

  // Recoverable error codes — user can fix by re-running consent.
  static RECOVERABLE_ERROR_CODES = [
    "FB_SCOPE_DECLINED",
    "FB_NO_PAGES_SELECTED",
    "FB_USER_CANCELLED",
    "FB_OAUTH_ERROR",
    "FB_INVALID_STATE",
    "FB_NO_TOKEN",
    "IG_PUBLISH_SCOPE_DECLINED",
    "IG_USER_CANCELLED",
    "IG_OAUTH_ERROR",
    "IG_INVALID_STATE",
    "IG_NO_USER_ID",
    "META_TOKEN_INVALID",
    "META_TOKEN_EXPIRED",
    "META_SESSION_EXPIRED",
    "META_USER_UNINSTALLED_APP",
    "META_PASSWORD_CHANGED",
    "META_PERMISSION_DENIED",
    "META_INVALID_PARAMETER",
    "LI_USER_CANCELLED",
    "LI_INVALID_STATE",
    "LI_TOKEN_EXCHANGE_FAILED",
    "LI_NO_TOKEN",
    "LI_USERINFO_FAILED",
    "LI_FETCH_ORGS_FAILED",
    "LI_OAUTH_ERROR",
    "LI_RATE_LIMIT",
    "LI_MEDIA_UPLOAD_FAILED",
    "LI_SERVICE_UNAVAILABLE",
    "LI_UNKNOWN_ERROR",
  ];

  handleCallbackError(errorCode, provider, detail) {
    const titleKey = `accountsPage.connect_errors.${errorCode}_title`;
    const messageKey = `accountsPage.connect_errors.${errorCode}`;

    const title = i18n.exists(titleKey)
      ? i18n.t(titleKey)
      : i18n.t("accountsPage.connectionError");

    let message;
    if (errorCode === ACCOUNT_LIMIT_REDIRECT_CODE) {
      message = this.accountLimitMessage();
    } else if (i18n.exists(messageKey)) {
      message = i18n.t(messageKey);
    } else if (detail) {
      message = detail;
    } else {
      message = i18n.t("accountsPage.unknownError");
    }

    const recoverable =
      AccountsPage.RECOVERABLE_ERROR_CODES.includes(errorCode) &&
      (provider === "facebook" ||
        provider === "instagram" ||
        provider === "linkedin");

    this.setState({
      callbackError: {
        title,
        message,
        code: errorCode,
        provider,
        detail,
        recoverable,
      },
      showCallbackBanner: true,
    });
  }

  handleLegacyCallbackError(errorType, description) {
    const map = {
      access_denied: ["accessDenied", "accessDeniedMessage"],
      server_error: ["serverError", "serverErrorMessage"],
      invalid_request: ["invalidRequest", "invalidRequestMessage"],
      callback_failed: ["callbackFailed", "callbackFailedMessage"],
      no_pages_found: ["noPagesFound", "noPagesFoundMessage"],
    };
    const [titleKey, messageKey] = map[errorType] || [];
    const title = titleKey
      ? i18n.t(`accountsPage.${titleKey}`)
      : i18n.t("accountsPage.connectionError");
    const message = messageKey
      ? i18n.t(`accountsPage.${messageKey}`)
      : description || i18n.t("accountsPage.unknownError");

    this.setState({
      callbackError: { title, message, code: errorType, recoverable: false },
      showCallbackBanner: true,
    });
  }

  closeCallbackBanner = () => {
    this.setState({ showCallbackBanner: false });
  };

  reconnectProvider = () => {
    const { callbackError } = this.state;
    if (!callbackError?.provider) return;
    this.setState({ showCallbackBanner: false });
    this.addAccount(callbackError.provider);
  };

  handleCallbackSuccess(added, warningsParam, provider) {
    const addedCount = Number(added);
    const message = Number.isFinite(addedCount)
      ? i18n.t("accountsPage.callbackSuccessAdded").replace("$1", addedCount)
      : i18n.t("accountsPage.callbackSuccess");

    const warnings = warningsParam
      ? warningsParam.split(";").map((s) => s.trim()).filter(Boolean)
      : [];

    this.setState({
      callbackSuccess: { message, warnings, provider },
      showSuccessBanner: true,
    });

    // Auto-hide only if there were no per-item warnings — otherwise let the user read them.
    if (warnings.length === 0) {
      setTimeout(() => {
        this.setState({ showSuccessBanner: false });
      }, 6000);
    }

    this.refresh?.();
  }

  closeSuccessBanner = () => {
    this.setState({ showSuccessBanner: false });
  };

  getAddNameContent(onClick, hideCb) {
    return this.insertGlobalInput(
      {
        inputRef: (el) => (this.newFolderName = el),
        placeholder: i18n.t("accountsPage.telegramKey"),
        autoFocus: true,
        size: "small",
        sx: { minWidth: "400px" },
        InputProps: {
          sx: {
            color: "var(--text-color)",
            backgroundColor: "var(--background)",
            border: "1px solid var(--text-color)",
            borderRadius: "7px",
            minWidth: "400px",
          },
        },
      },
      {
        onEnter: () => {
          hideCb();
          onClick();
        },
      },
    );
  }

  openAddNameDialog(title, label, onClick) {
    this.openEditDialog("pageDialogProps", {
      title,
      label,
      onClick,
      text: this.getAddNameContent.bind(
        this,
        onClick,
        this.changeState.bind(this, "pageDialogProps", null),
      ),
    });
  }

  addAccount(social_type) {
    if (social_type === "telegram") {
      return this.openTelegramDialog();
    }

    if (social_type === "linkedin") {
      return this.openLinkedInModeDialog();
    }

    const acc = this.formNewAcc(social_type);

    if (social_type === "instagram") {
      return this.openInstagramBusinessDialog(acc);
    }

    this.addAccountSend(acc);
  }

  // The analytics of one account, in the page's dialog instead of a new route (OP#3456).
  openAnalyticsDialog = (info) => {
    this.openEditDialog("pageDialogProps", {
      title: "analytics.dialogTitle",
      label: "",
      onClick: () => {},
      text: () => (
        <AccountAnalytics
          key={info.id}
          accounts={[info]}
          accountId={info.id}
          onReconnect={(account) => {
            this.changeState("pageDialogProps", null);
            this.addAccount(account.social_type);
          }}
        />
      ),
      selfProps: { buttons: [{ label: "analytics.close", variant: "outlined" }] },
    });
  };

  openTelegramDialog() {
    this.openEditDialog("pageDialogProps", {
      title: "accountsPage.addTelegram",
      label: "",
      onClick: () => {},
      text: () => (
        <TelegramChatSelector
          onSuccess={() => {
            this.changeState("pageDialogProps", null);
            this.refresh();
          }}
          onCancel={() => this.changeState("pageDialogProps", null)}
        />
      ),
      selfProps: {
        buttons: [],
      },
    });
  }

  openInstagramBusinessDialog(acc) {
    this.openEditDialog("pageDialogProps", {
      title: i18n.t("accountsPage.instagramBusinessRequired"),
      label: i18n.t("accountsPage.understand"),
      onClick: () => {
        this.addAccountSend(acc);
        this.changeState("pageDialogProps", null);
      },
      text: () => i18n.t("accountsPage.instagramBusinessNote"),
    });
  }

  openLinkedInModeDialog() {
    const pick = (account_type) => {
      this.changeState("pageDialogProps", null);
      this.addAccountSend({ social_type: "linkedin", account_type });
    };

    const optionSx = {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      gap: "4px",
      padding: "14px 16px",
      borderRadius: "10px",
      border: "1px solid var(--border-color, rgba(255,255,255,0.15))",
      cursor: "pointer",
      transition: "background-color 0.15s ease",
      "&:hover": { backgroundColor: "rgba(66, 192, 199, 0.08)" },
    };
    const optionTitleSx = {
      color: "var(--text-color)",
      fontWeight: 600,
      fontSize: "15px",
    };
    const optionDescSx = {
      color: "var(--text-color)",
      opacity: 0.7,
      fontSize: "13px",
      lineHeight: 1.4,
    };

    this.openEditDialog("pageDialogProps", {
      title: "accountsPage.linkedinModeTitle",
      label: "",
      onClick: () => {},
      text: () => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px", minWidth: "360px", maxWidth: "440px" }}>
          <Box sx={optionSx} onClick={() => pick("profile")}>
            <Typography sx={optionTitleSx}>
              {i18n.t("accountsPage.linkedinModeProfile")}
            </Typography>
            <Typography sx={optionDescSx}>
              {i18n.t("accountsPage.linkedinModeProfileHint")}
            </Typography>
          </Box>
          <Box sx={optionSx} onClick={() => pick("page")}>
            <Typography sx={optionTitleSx}>
              {i18n.t("accountsPage.linkedinModePage")}
            </Typography>
            <Typography sx={optionDescSx}>
              {i18n.t("accountsPage.linkedinModePageHint")}
            </Typography>
          </Box>
        </Box>
      ),
      selfProps: { buttons: [] },
    });
  }

  addAccountTelegram(acc) {
    acc.access_token = this.newFolderName.value;

    if (acc.access_token) this.addAccountSend(acc);
  }

  formNewAcc(social_type) {
    if (social_type === "instagram") {
      return { social_type, account_type: "business" };
    }

    return { social_type, account_type: "profile" };
  }

  addAccountSend(info) {
    _request.accounts.loading(async () => {
      try {
        const response = await _request.accounts.add(info);

        // Проверяем успешный ответ
        if (response.isOk) {
          const url =
            typeof response.data === "string"
              ? response.data
              : typeof response === "string"
                ? response
                : null;

          if (url) {
            if (response.data?.social_type !== "telegram") {
              // Добавляем параметр для отслеживания источника
              const separator = url.includes("?") ? "&" : "?";
              const trackingUrl = `${url}${separator}source=connexxtion`;
              window.location.href = trackingUrl;
            } else {
              this.refresh();
            }
          } else {
            this.showErrorDialog(
              i18n.t("accountsPage.connectionError"),
              i18n.t("accountsPage.unknownError"),
            );
          }
        } else {
          // Обрабатываем ошибки от сервера
          this.handleServerError(response);
        }
      } catch (error) {
        console.error("Network error:", error);
        this.showErrorDialog(
          i18n.t("accountsPage.serverError"),
          i18n.t("accountsPage.serverErrorMessage"),
        );
      }
    });
  }

  // The plan's account limit (OP#2997 / OP#3009 / OP#3020): the same explanation
  // whether the refusal came from /accounts/add or from the OAuth callback.
  accountLimitMessage() {
    return isAppleBilled(this.state.metaData)
      ? i18n.t("accountsPage.connect_errors.ACCOUNT_LIMIT_appstore")
      : i18n.t("accountsPage.connect_errors.ACCOUNT_LIMIT");
  }

  handleServerError(response) {
    if (response?.isAccountLimit) {
      this.showErrorDialog(
        i18n.t("accountsPage.connect_errors.ACCOUNT_LIMIT_title"),
        this.accountLimitMessage(),
      );
      return;
    }

    let title = "Ошибка подключения";
    let message = "Произошла неизвестная ошибка. Попробуйте еще раз.";

    // Если есть данные об ошибке в response.data (как в вашем примере)
    if (response.data && typeof response.data === "string") {
      message = response.data;
    } else if (response.message) {
      message = response.message;
    } else if (response.error) {
      // Обрабатываем различные типы ошибок согласно документации
      switch (response.error) {
        case "access_denied":
          title = "Доступ отклонён";
          message =
            "Доступ к социальной сети был отклонён. Для подключения необходимо предоставить разрешения.";
          break;
        case "no_pages_found":
          title = "Страницы не найдены";
          message =
            response.data ||
            "У вашего аккаунта Facebook нет доступных страниц для подключения. Убедитесь, что у вас есть страница Facebook и вы являетесь её администратором.";
          break;
        case "server_error":
          title = "Ошибка сервера";
          message =
            "Временная ошибка сервера. Попробуйте подключиться через несколько минут.";
          break;
        case "invalid_request":
          title = "Некорректный запрос";
          message = "Некорректный запрос. Попробуйте повторить подключение.";
          break;
        case "token_exchange_failed":
          title = "Ошибка авторизации";
          message =
            "Неверный запрос к социальной сети. Проверьте настройки приложения.";
          break;
        default:
          message = response.error;
      }
    }

    // Обрабатываем HTTP коды ошибок
    if (response.code) {
      switch (response.code) {
        case 400:
          if (!message.includes("неверн") && !message.includes("некорректн")) {
            title = "Неверный запрос";
            message = "Неверный запрос к серверу. Попробуйте еще раз.";
          }
          break;
        case 401:
          title = "Ошибка авторизации";
          message = "Ошибка авторизации. Попробуйте войти в систему заново.";
          break;
        case 403:
          title = "Доступ запрещён";
          message = "У вас нет прав для выполнения этого действия.";
          break;
        case 429:
          title = "Превышен лимит";
          message =
            "Превышен лимит запросов. Попробуйте через несколько минут.";
          break;
        case 500:
          title = "Ошибка сервера";
          message = "Внутренняя ошибка сервера. Попробуйте позже.";
          break;
        default:
          // Оставляем текущие title и message без изменений
          break;
      }
    }

    this.showErrorDialog(title, message);
  }

  showErrorDialog(title, message) {
    this.setState({
      errorDialogOpen: true,
      errorTitle: title,
      errorMessage: message,
    });
  }

  closeErrorDialog = () => {
    this.setState({
      errorDialogOpen: false,
      errorTitle: "",
      errorMessage: "",
    });
  };

  openDeleteDialog = (info) => {
    this.setState({ deleteDialogOpen: true, accountToDelete: info });
  };

  closeDeleteDialog = () => {
    this.setState({ deleteDialogOpen: false, accountToDelete: null });
  };

  confirmDelete = () => {
    const { accountToDelete } = this.state;
    this.deleteAccount(accountToDelete);
    this.closeDeleteDialog();
  };

  confirmDialog = () => {
    const { deleteDialogOpen } = this.state;

    return (
      <Dialog
        open={deleteDialogOpen}
        onClose={this.closeDeleteDialog}
        PaperProps={{
          style: {
            backgroundColor: "var(--background-menu)",
          },
        }}
        BackdropProps={{
          style: {
            opacity: "0.5",
          },
        }}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description">
        <DialogTitle sx={{ color: "var(--text-color)" }}>
          {i18n.t("accountsPage.confirmDeletion")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "var(--text-color)" }}>
            {i18n.t("dialog.accountRemove")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box className="cancel-button" onClick={this.closeDeleteDialog}>
            {i18n.t("cancel")}
          </Box>
          <Box className="confirm-button" onClick={this.confirmDelete}>
            {i18n.t("confirm")}
          </Box>
        </DialogActions>
      </Dialog>
    );
  };

  openWarningDialog = (info) => {
    this.setState({
      deleteWarningOpen: true,
      accountToDelete: info,
    });
  };

  closeWarningDialog = () => {
    this.setState({ deleteWarningOpen: false, accountToDelete: null });
  };

  warningDialog = () => {
    const { deleteWarningOpen } = this.state;

    return (
      <Dialog
        open={deleteWarningOpen}
        onClose={this.closeWarningDialog}
        PaperProps={{
          style: {
            backgroundColor: "var(--background-menu)",
          },
        }}
        BackdropProps={{
          style: {
            opacity: "0.5",
          },
        }}
        aria-labelledby="confirmation-dialog-title"
        aria-describedby="confirmation-dialog-description">
        <DialogTitle sx={{ color: "var(--text-color)" }}>
          {i18n.t("accountsPage.warning")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--text-color)" }}>
            {i18n.t("accountsPage.noPermissionDelete")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Box className="confirm-button" onClick={this.closeWarningDialog}>
            {i18n.t("accountsPage.close")}
          </Box>
        </DialogActions>
      </Dialog>
    );
  };

  deleteAccount(info) {
    const { id } = info;
    const { companyType } = this.props;
    const { userTeam, metaData } = this.state;

    const myId = metaData?.user?.id;
    const myMembership = myId
      ? userTeam.find((ut) => ut.userid === myId && ut.status === 1)
      : null;
    const permissions = myMembership?.permissions || {};

    const allowed =
      companyType === "invited" || permissions["manage-accounts"] === "1";

    if (!allowed) {
      this.openWarningDialog(info);
      return;
    }

    _request.accounts.loading(async () => {
      const resp = await _request.accounts.delete(id, { silent: true });
      if (resp?.isOk) {
        this.refresh();
      } else {
        this.openWarningDialog(info);
      }
    });
  }

  setDeleteMenuOpened = (deleteMenuOpened) => {
    this.setState({ deleteMenuOpened });
  };

  insertItem(key, info) {
    const { id, username, account_name, avatar, social_type, account_type, state } = info;
    const imageSrc = _request.accounts.formLinkSocial(id, 300);
    const { deleteMenuOpened } = this.state;
    const open = Boolean(deleteMenuOpened);
    const closeCb = () => this.setDeleteMenuOpened(null);
    const isDeleted = state === "deleted";
    // The server flags an account whose OAuth token stopped working (OP#2445/#2446), so
    // the user finds out here instead of from a post that quietly failed overnight.
    const needsReconnect = state === "needs_reconnect";
    const isCompanyPage = social_type === "linkedin" && account_type === "page";
    // Threads chains need the replies permission (OP#3465). Ordinary posts keep working without it, so this is a
    // quiet hint, not the orange "needs reconnect" state.
    const threadsRepliesReconnect =
      social_type === "threads" && info.threads_replies === "reconnect_required" && !isDeleted && !needsReconnect;
    // A badge across the top of the cover leaves less room for the avatar.
    const hasTopBadge = isDeleted || needsReconnect || isCompanyPage;
    // The caption is clamped, so the whole text goes into the tooltip.
    const fullTitle = [...new Set([username, account_name].filter(Boolean))].join(" · ");

    return (
      <Box
        key={key}
        sx={{
          width: "176px",
          height: "176px",
          borderRadius: "5px",
          backgroundImage: `url(${imageSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          zIndex: 1,
          opacity: isDeleted ? 0.6 : 1,
          filter: isDeleted ? "grayscale(1)" : "none",
          outline: needsReconnect ? "2px solid rgba(237, 108, 2, 0.95)" : "none",
          outlineOffset: needsReconnect ? "-2px" : 0,
        }}>
        {needsReconnect && !isDeleted && (
          <Box
            title={i18n.t("accountsPage.needsReconnectHint")}
            sx={{
              position: "absolute",
              top: "8px",
              left: "8px",
              right: "8px",
              backgroundColor: "rgba(237, 108, 2, 0.95)",
              color: "white",
              fontSize: "11px",
              fontWeight: "bold",
              padding: "2px 6px",
              borderRadius: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              textAlign: "center",
              zIndex: 3,
            }}>
            {i18n.t("accountsPage.needsReconnect")}
          </Box>
        )}
        {isDeleted && (
          <Box
            sx={{
              position: "absolute",
              top: "8px",
              left: "8px",
              backgroundColor: "rgba(244, 67, 54, 0.9)",
              color: "white",
              fontSize: "11px",
              fontWeight: "bold",
              padding: "2px 6px",
              borderRadius: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              zIndex: 3,
            }}>
            {i18n.t("accountsPage.deleted")}
          </Box>
        )}
        {isCompanyPage && !isDeleted && !needsReconnect && (
          <Box
            sx={{
              position: "absolute",
              top: "8px",
              left: "8px",
              backgroundColor: "rgba(4, 120, 176, 0.92)",
              color: "white",
              fontSize: "11px",
              fontWeight: "bold",
              padding: "2px 6px",
              borderRadius: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              zIndex: 3,
            }}>
            {i18n.t("accountsPage.linkedinBadgeCompany")}
          </Box>
        )}
        <Box
          sx={{
            height: "84px",
            flex: "0 0 auto",
            position: "relative",
            display: "flex",
            alignItems: hasTopBadge ? "flex-end" : "center",
            justifyContent: "center",
            paddingBottom: hasTopBadge ? "6px" : 0,
          }}>
          <AvatarIcon avatar={avatar} socialType={social_type} width={hasTopBadge ? "48px" : "56px"} />
          <Box
            sx={{
              position: "absolute",
              right: "8px",
              bottom: "8px",
              width: "26px",
              height: "26px",
              borderRadius: "8px",
              backgroundColor: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
            <IconsAccounts width={18} height={18} socialType={social_type} />
          </Box>
        </Box>
        <Box
          title={fullTitle}
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            padding: "8px 12px",
            backgroundColor: "rgba(0, 0, 0, 0.35)",
          }}>
          <Typography
            sx={{
              color: "white",
              fontSize: "13px",
              fontWeight: "bold",
              lineHeight: 1.25,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: (needsReconnect && !isDeleted) || threadsRepliesReconnect ? 1 : 2,
              overflow: "hidden",
              overflowWrap: "anywhere",
              textAlign: "left",
            }}>
            {username}
          </Typography>
          {needsReconnect && !isDeleted ? (
            <Box
              onClick={(event) => {
                event.stopPropagation();
                this.addAccount(social_type);
              }}
              sx={{
                marginTop: "auto",
                backgroundColor: "rgba(237, 108, 2, 0.95)",
                color: "white",
                fontSize: "12px",
                fontWeight: "bold",
                padding: "4px 6px",
                borderRadius: "4px",
                textAlign: "center",
                cursor: "pointer",
              }}>
              {i18n.t("accountsPage.reconnect")}
            </Box>
          ) : threadsRepliesReconnect ? (
            <Box
              component="button"
              type="button"
              title={i18n.t("accountsPage.threadsRepliesReconnect")}
              aria-label={i18n.t("accountsPage.threadsRepliesReconnect")}
              onClick={(event) => {
                event.stopPropagation();
                this.addAccount(social_type);
              }}
              sx={{
                marginTop: "auto",
                backgroundColor: "rgba(255, 255, 255, 0.16)",
                border: "1px solid rgba(255, 255, 255, 0.6)",
                color: "white",
                fontSize: "11px",
                fontWeight: 600,
                fontFamily: "inherit",
                lineHeight: 1.25,
                padding: "3px 6px",
                borderRadius: "4px",
                textAlign: "center",
                cursor: "pointer",
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.28)" },
              }}>
              {i18n.t("accountsPage.threadsRepliesReconnect")}
            </Box>
          ) : (
            account_name && (
              <Typography
                noWrap
                sx={{
                  color: "rgba(255, 255, 255, 0.82)",
                  fontSize: "12px",
                  lineHeight: 1.3,
                  marginTop: "2px",
                  textAlign: "left",
                }}>
                {account_name}
              </Typography>
            )
          )}
        </Box>
        <Box
          sx={{
            position: "absolute",
            top: "3px",
            right: "8px",
            cursor: "pointer",
            zIndex: 2,
          }}
          onClick={(event) => {
            this.setDeleteMenuOpened(event.currentTarget);
            this.selectedAccount = info;
          }}>
          <MoreHorizontalIcon style={{ color: "white", fontSize: "32px" }} />
        </Box>
        <Menu
          anchorEl={deleteMenuOpened}
          open={open}
          onClose={closeCb}
          onClick={closeCb}
          PaperProps={{
            style: {
              backgroundColor: "var(--hedear-bg)",
            },
          }}>
          {this.selectedAccount?.state !== "deleted" && (
            <MenuItem
              onClick={() => this.openAnalyticsDialog(this.selectedAccount)}
              sx={{ color: "var(--text-color)" }}>
              {i18n.t("analytics.menuItem")}
            </MenuItem>
          )}
          <MenuItem
            onClick={() => this.openDeleteDialog(this.selectedAccount)}
            sx={{ color: "var(--text-color)" }}>
            {i18n.t("delete")}
          </MenuItem>
        </Menu>
        {this.confirmDialog()}
        {this.warningDialog()}
      </Box>
    );
  }

  retrieveItems(all) {
    return (
      <React.Fragment>
        {all.map((item, i) => this.insertItem(i, item))}
      </React.Fragment>
    );
  }

  setProfileMenuOpened(profileMenuOpened) {
    this.setState({ profileMenuOpened });
  }

  accountsMenu() {
    const { profileMenuOpened } = this.state;

    const isDev = ["localhost", "dev.connexxtion.ch"].includes(window.location.hostname);
    const COMING_SOON = isDev ? [] : ["twitter", "tiktok"];

    // Показываем все доступные социальные сети
    const accountsToShow = Object.keys(socialAccounts);

    const open = Boolean(profileMenuOpened);
    const closeCb = () => this.setProfileMenuOpened(null);

    return (
      <Menu
        anchorEl={profileMenuOpened}
        id="account-menu"
        open={open}
        onClose={closeCb}
        onClick={closeCb}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            borderRadius: "20px",
            background: "var(--background-menu-list)",
            padding: "20px",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "var(--background-menu-list)",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "8px",
            justifyItems: "center",
            alignItems: "center",
            width: "100%",
            maxWidth: "300px",
          }}>
          {accountsToShow.map((it, i) => {
            const isComingSoon = COMING_SOON.includes(it);
            return (
              <MenuItem
                key={i}
                onClick={isComingSoon ? undefined : this.addAccount.bind(this, it)}
                sx={{
                  padding: "8px",
                  minHeight: "70px",
                  width: "70px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  transition: "background-color 0.2s ease",
                  cursor: isComingSoon ? "default" : "pointer",
                  position: "relative",
                  "&:hover": {
                    backgroundColor: isComingSoon ? "transparent" : "rgba(66, 192, 199, 0.1)",
                  },
                }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "4px",
                    textAlign: "center",
                    opacity: isComingSoon ? 0.45 : 1,
                  }}>
                  <IconsAccounts width={32} height={32} socialType={it} />
                  <Typography
                    sx={{
                      fontSize: "10px",
                      color: "var(--text-color)",
                      fontWeight: 500,
                      lineHeight: 1.2,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "60px",
                    }}>
                    {it}
                  </Typography>
                </Box>
                {isComingSoon && (
                  <Typography
                    sx={{
                      position: "absolute",
                      bottom: "4px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      fontSize: "8px",
                      fontWeight: 600,
                      color: "#fff",
                      backgroundColor: "rgba(0,0,0,0.55)",
                      borderRadius: "4px",
                      padding: "1px 4px",
                      whiteSpace: "nowrap",
                      pointerEvents: "none",
                    }}>
                    soon
                  </Typography>
                )}
              </MenuItem>
            );
          })}
        </Box>
      </Menu>
    );
  }

  renderTopPanel(itemsLength) {
    return (
      <Box className="flex between centerV bgDarkTheme pad10 h-[5.313rem]">
        <Box className="flex1 flex gap8 font f28 col black07">
          <Typography
            className="page-title"
            sx={{ padding: 5, color: "var(--text-color)" }}>
            {i18n.t("accountsPage.accounts")}
          </Typography>
        </Box>
        {itemsLength > 0 && (
          <Box className="flex gap8">
            <Box
              className="btnDark"
              onClick={(event) => {
                this.setProfileMenuOpened(event.currentTarget);
              }}>
              {i18n.t("accountsPage.addNew")}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  addUpdateGroup(id) {
    const title = this.newGroupName.value;
    const accounts = this.state.selectedAccounts;

    if (accounts.length === 0) {
      _request.groups.loading(async () => {
        await _request.groups.delete(id);
        this.refresh();
      });
      return;
    }

    const obj = { title, accounts };
    if (id) obj.id = id;

    _request.groups.loading(async () => {
      await _request.groups.addUpdate(obj);
      this.refresh();
    });
  }

  deleteGroup({ id }) {
    _helper.confirm(
      "dialog.groupRemove",
      this.deleteGroupConfirmed.bind(this, id),
    );
  }

  deleteGroupConfirmed(id) {
    _request.groups.loading(async () => {
      await _request.groups.delete(id);
      this.refresh();
    });
  }

  getEditFolderContent(tProps, onClick, defaultId, hideCb) {
    const { items, selectedAccounts } = this.state;
    const { companyType } = this.props;

    return (
      <Box>
        {companyType === "invited" ? (
          <Box className="flex column gap16">
            {this.insertGlobalInput(
              {
                inputRef: (el) => (this.newGroupName = el),
                placeholder: i18n.t("groupsPage.groupName"),
                autoFocus: true,
                size: "small",
                InputProps: {
                  sx: {
                    color: "var(--text-color)",
                    backgroundColor: "var(--background)",
                    border: "1px solid var(--text-color)",
                    borderRadius: "7px",
                    minWidth: "400px",
                  },
                },
                ...tProps,
              },
              {
                onEnter: () => {
                  hideCb();
                  onClick();
                },
              },
            )}
            <Box className="flex column gap4">
              <Box className="font w500 f14">
                {i18n.t("pages.accounts") + ":"}
              </Box>
              <Box
                className="flex column border black07 rad5 pad5 scrolling"
                style={{ maxHeight: "50vh" }}>
                {items.map((info, i) => {
                  const { id, username, social_type } = info;
                  const checked = selectedAccounts.includes(id);

                  return (
                    <Box
                      key={i}
                      className="flex gap8 centerV"
                      onClick={() =>
                        this.toggleStateArray("selectedAccounts", id)
                      }>
                      <IconsAccounts
                        width={24}
                        height={24}
                        socialType={social_type}
                      />
                      <Typography component="span" className="flex1">
                        {username}
                      </Typography>
                      <Checkbox color="primary" checked={checked} />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        ) : (
          <Box className="flex flex-col items-center justify-center p-6 text-center max-w-[450px]">
            <Typography variant="h6" className="font-semibold w500 mb-4">
              {i18n.t("accountsPage.importantNotice")}
            </Typography>
            <Typography variant="body1" className="font w300">
              {i18n.t("accountsPage.groupPermissionNote")}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  openEditFolderDialog(label, onClick, tProps = {}, accounts, defaultId) {
    this.state.selectedAccounts = accounts.map((it) => parseInt(it));

    this.openEditDialog("pageDialogProps", {
      title: "groupsPage.group",
      label,
      onClick,
      text: this.getEditFolderContent.bind(
        this,
        tProps,
        onClick,
        defaultId,
        this.changeState.bind(this, "pageDialogProps", null),
      ),
    });
  }

  addGroupShow() {
    this.openEditFolderDialog(
      "create",
      this.addUpdateGroup.bind(this),
      { defaultValue: "" },
      [],
    );
  }

  editGroupShow(info) {
    const defaultId = info?.id || 0;
    const defaultValue = info?.title || "";
    const accounts = info?.accounts ? info.accounts : [];
    this.openEditFolderDialog(
      "save",
      this.addUpdateGroup.bind(this, info?.id),
      { defaultValue },
      accounts,
      defaultId,
    );
  }

  // The group stays in the state after the menu closes, so the menu does not lose it while it fades out.
  setDeleteGroupOpened = (deleteGroupOpened, group) => {
    this.setState((prev) => ({
      deleteGroupOpened,
      deleteGroupTarget: group || prev.deleteGroupTarget,
    }));
  };
  groupsItems(groups, items) {
    const { deleteGroupOpened, deleteGroupTarget } = this.state;
    const closeCb = () => this.setDeleteGroupOpened(null);

    return (
      <React.Fragment>
        <Box className="flex items-center">
          {groups.map((group, i) => {
            const matchingAccounts = group.accounts.map((accountId) => {
              return items.find((item) => item.id === accountId);
            });

            const filteredMatchingAccounts = matchingAccounts.filter(
              (account) => account !== undefined && account !== null,
            );

            return (
              <React.Fragment key={i}>
                <Box
                  className="grid mr-5 p-3"
                  sx={{
                    backgroundColor: "var(--hedear-bg)",
                    width: "max-content",
                    borderRadius: "10px",
                    border: "4px solid #42C0C7",
                    position: "relative",
                  }}>
                  <Box className="flex items-center gap-2 mb-5 p-3">
                    {filteredMatchingAccounts.map((account, j) => (
                      <Box key={j} sx={{ position: "relative" }}>
                        <AvatarIcon
                          avatar={account.avatar}
                          socialType={account.social_type}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: "-8px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            zIndex: 1,
                          }}>
                          <IconsAccounts
                            width={22}
                            height={22}
                            socialType={account.social_type}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: 20,
                      textAlign: "center",
                      color: "var(--text-color)",
                    }}>
                    {group.title}
                  </Typography>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-2px",
                      right: "3px",
                      cursor: "pointer",
                      zIndex: 2,
                    }}
                    onClick={(event) => {
                      this.setDeleteGroupOpened(event.currentTarget, group);
                    }}>
                    <MoreHorizontalIcon
                      style={{ color: "white", fontSize: "32px" }}
                    />
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
        <GroupActionsMenu
          anchorEl={deleteGroupOpened}
          group={deleteGroupTarget}
          onClose={closeCb}
          onEdit={(group) => this.editGroupShow(group)}
          onDelete={(group) => this.deleteGroup(group)}
        />
      </React.Fragment>
    );
  }

  insertPageGroups() {
    return (
      <Box className="flex items-center mb-5 mt-5">
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: "bold",
            marginRight: "15px",
            color: "var(--text-color)",
          }}>
          {i18n.t("accountsPage.groupOfAccounts")}
        </Typography>
        <Button
          className="flex items-center"
          onClick={this.addGroupShow.bind(this)}>
          <AddIcon style={{ color: "#42C0C7" }} />
          <Typography
            sx={{
              color: "#42C0C7",
              fontSize: 15,
              fontWeight: 600,
              textTransform: "Capitalize",
            }}>
            {i18n.t("add")}
          </Typography>
        </Button>
      </Box>
    );
  }

  handleClickOpen() {
    this.setState({ dialogOpen: true });
  }

  handleClose() {
    this.setState({ dialogOpen: false });
  }

  addAccountShow() {
    const { dialogOpen } = this.state;

    const isDev = ["localhost", "dev.connexxtion.ch"].includes(window.location.hostname);
    const COMING_SOON = isDev ? [] : ["twitter", "tiktok"];

    // Показываем все доступные социальные сети
    const accountsToShow = Object.keys(socialAccounts);

    return (
      <Dialog open={dialogOpen} onClose={this.handleClose}>
        <DialogContent sx={{ backgroundColor: "var(--background-menu)" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gridGap: "10px",
              justifyItems: "center",
            }}>
            {accountsToShow.map((it, i) => {
              const isComingSoon = COMING_SOON.includes(it);
              return (
                <Box
                  aria-hidden="true"
                  key={i}
                  onClick={isComingSoon ? undefined : this.addAccount.bind(this, it)}
                  sx={{ position: "relative", cursor: isComingSoon ? "default" : "pointer" }}>
                  <Box sx={{ display: "grid", justifyItems: "center", opacity: isComingSoon ? 0.45 : 1 }}>
                    <IconsAccounts width={32} height={32} socialType={it} />
                    <Typography
                      sx={{ fontSize: "12px", color: "var(--text-color)" }}>
                      {it}
                    </Typography>
                  </Box>
                  {isComingSoon && (
                    <Typography
                      sx={{
                        position: "absolute",
                        bottom: "-4px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        fontSize: "9px",
                        fontWeight: 600,
                        color: "#fff",
                        backgroundColor: "rgba(0,0,0,0.55)",
                        borderRadius: "4px",
                        padding: "1px 4px",
                        whiteSpace: "nowrap",
                        pointerEvents: "none",
                      }}>
                      soon
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: "var(--background-menu)" }}>
          <Button onClick={this.handleClose}>{i18n.t("cancel")}</Button>
        </DialogActions>
      </Dialog>
    );
  }

  renderPage() {
    const { items, groups, metaData } = this.state;
    const visibleItems = (items || []).filter(
      (item) => item?.state !== "deleted",
    );
    const itemsLength = visibleItems.length;
    const isTrialEnded = (metaData.user?.package ?? metaData.package) === 8;
    const isPaused = (metaData.user?.package ?? metaData.package) === 9;

    return (
      <Box className="flex1 flex column">
        {this.renderTopPanel(itemsLength)}

        {(isTrialEnded || isPaused) && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}>
            <Box
              sx={{
                backgroundColor: "var(--background-menu)",
                padding: "20px",
                borderRadius: "10px",
                boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
                textAlign: "center",
              }}>
              {isTrialEnded ? (
                <>
                  <Typography variant="h6" color="error">
                    {i18n.t("accountsPage.trialEnded")}
                  </Typography>
                  <Link to="/app/pricing">
                    <Button variant="contained" color="primary" sx={{ mt: 2 }}>
                      {i18n.t("accountsPage.upgradeNow")}
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Typography variant="h6" color="warning">
                    {i18n.t("accountsPage.subscriptionPaused")}
                  </Typography>
                  <Link to="/app/pricing">
                    <Button
                      variant="contained"
                      color="secondary"
                      sx={{ mt: 2 }}>
                      {i18n.t("accountsPage.resumeSubscription")}
                    </Button>
                  </Link>
                </>
              )}
            </Box>
          </Box>
        )}

        {itemsLength === 0 ? (
          <Box
            className="flex column items-center justify-center w-full h-full"
            sx={{ backgroundColor: "var(--background-menu-list)" }}>
            <EmptyAcc />
            <Typography
              variant="body1"
              className="font w300"
              sx={{ color: "var(--text-color)" }}>
              {i18n.t("accountsPage.oopsEmpty")}
            </Typography>
            <Typography
              variant="body2"
              className="font w300"
              sx={{ color: "var(--text-login)" }}>
              {i18n.t("accountsPage.noAccountsFound")}
            </Typography>
            <Box
              className="btnDark mt-4"
              sx={{ borderRadius: "23px" }}
              onClick={this.handleClickOpen}>
              {i18n.t("accountsPage.addAccount")}
            </Box>
            {this.addAccountShow()}
          </Box>
        ) : (
          <Box className="mt-6" sx={{ padding: "0 50px" }}>
            {this.accountsMenu()}
            {this.insertPageHeader("accounts")}
            <Box className="flex startV wrap startC gap24 h-fit">
              {this.retrieveItems(visibleItems)}
            </Box>
            <Box>
              {this.insertPageGroups()}
              {this.groupsItems(groups, visibleItems)}
            </Box>
          </Box>
        )}
        {this.errorDialog()}
        {this.callbackSuccessBanner()}
        {this.callbackErrorBanner()}
      </Box>
    );
  }

  callbackSuccessBanner = () => {
    const { showSuccessBanner, callbackSuccess } = this.state;

    if (!showSuccessBanner || !callbackSuccess) {
      return null;
    }

    return (
      <Box
        sx={{
          position: "fixed",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1500,
          minWidth: "400px",
          maxWidth: "600px",
          backgroundColor: "var(--background-menu)",
          border: "1px solid #4caf50",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}>
        <Box
          sx={{
            width: "6px",
            height: "40px",
            backgroundColor: "#4caf50",
            borderRadius: "3px",
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              color: "var(--text-color)",
              fontSize: "16px",
              fontWeight: "bold",
              marginBottom: "4px",
            }}>
            {i18n.t("accountsPage.callbackSuccessTitle")}
          </Typography>
          <Typography
            sx={{
              color: "var(--text-color)",
              fontSize: "14px",
              opacity: 0.9,
              lineHeight: 1.4,
            }}>
            {callbackSuccess.message}
          </Typography>
          {callbackSuccess.warnings && callbackSuccess.warnings.length > 0 && (
            <Box sx={{ marginTop: "8px" }}>
              <Typography
                sx={{
                  color: "var(--text-color)",
                  fontSize: "13px",
                  fontWeight: 600,
                  opacity: 0.85,
                  marginBottom: "4px",
                }}>
                {i18n.t("accountsPage.callbackSkippedTitle")}
              </Typography>
              <Box
                component="ul"
                sx={{
                  margin: 0,
                  paddingLeft: "18px",
                  color: "var(--text-color)",
                  fontSize: "13px",
                  opacity: 0.85,
                  lineHeight: 1.4,
                }}>
                {callbackSuccess.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </Box>
            </Box>
          )}
        </Box>
        <Button
          onClick={this.closeSuccessBanner}
          className="btnDark"
          sx={{
            minWidth: "auto",
            padding: "6px",
          }}>
          ✕
        </Button>
      </Box>
    );
  };

  callbackErrorBanner = () => {
    const { showCallbackBanner, callbackError } = this.state;

    if (!showCallbackBanner || !callbackError) {
      return null;
    }

    return (
      <Box
        sx={{
          position: "fixed",
          bottom: "20px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 1500,
          minWidth: "400px",
          maxWidth: "600px",
          backgroundColor: "var(--background-menu)",
          border: "1px solid #ff6b6b",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          padding: "16px 20px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
        }}>
        <Box
          sx={{
            width: "6px",
            height: "40px",
            backgroundColor: "#ff6b6b",
            borderRadius: "3px",
            flexShrink: 0,
          }}
        />
        <Box sx={{ flex: 1 }}>
          <Typography
            sx={{
              color: "var(--text-color)",
              fontSize: "16px",
              fontWeight: "bold",
              marginBottom: "4px",
            }}>
            {callbackError.title}
          </Typography>
          <Typography
            sx={{
              color: "var(--text-color)",
              fontSize: "14px",
              opacity: 0.9,
              lineHeight: 1.4,
              whiteSpace: "pre-line",
            }}>
            {callbackError.message}
          </Typography>
          {callbackError.code === ACCOUNT_LIMIT_REDIRECT_CODE &&
            !isAppleBilled(this.state.metaData) && (
              <Box sx={{ marginTop: "10px" }}>
                <Button
                  component={Link}
                  to="/app/pricing"
                  variant="contained"
                  size="small"
                  className="btnDark"
                  sx={{ textTransform: "none" }}>
                  {i18n.t("accountsPage.seePlans")}
                </Button>
              </Box>
            )}
          {callbackError.recoverable && (
            <Box sx={{ marginTop: "10px" }}>
              <Button
                onClick={this.reconnectProvider}
                variant="contained"
                size="small"
                className="btnDark"
                sx={{ textTransform: "none" }}>
                {i18n.t("accountsPage.reconnect")}
              </Button>
            </Box>
          )}
          {callbackError.detail &&
            callbackError.code !== ACCOUNT_LIMIT_REDIRECT_CODE &&
            callbackError.message !== callbackError.detail && (
              <Typography
                sx={{
                  color: "var(--text-color)",
                  fontSize: "12px",
                  opacity: 0.65,
                  marginTop: "6px",
                }}>
                {callbackError.detail}
              </Typography>
            )}
        </Box>
        <Button
          onClick={this.closeCallbackBanner}
          className="btnDark"
          sx={{
            minWidth: "auto",
            padding: "6px",
          }}>
          ✕
        </Button>
      </Box>
    );
  };

  errorDialog = () => {
    const { errorDialogOpen, errorTitle, errorMessage } = this.state;

    return (
      <Dialog
        open={errorDialogOpen}
        onClose={this.closeErrorDialog}
        PaperProps={{
          style: {
            backgroundColor: "var(--background-menu)",
          },
        }}
        BackdropProps={{
          style: {
            opacity: "0.5",
          },
        }}
        maxWidth="sm"
        fullWidth
        aria-labelledby="error-dialog-title"
        aria-describedby="error-dialog-description">
        <DialogTitle
          sx={{
            color: "var(--text-color)",
            borderBottom: "1px solid var(--border-color)",
            paddingBottom: "16px",
          }}>
          {errorTitle}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: "20px" }}>
          <Typography
            sx={{
              color: "var(--text-color)",
              lineHeight: 1.6,
              fontSize: "16px",
            }}>
            {errorMessage}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button
            onClick={this.closeErrorDialog}
            variant="contained"
            className="btnDark">
            {i18n.t("accountsPage.understand") || "Понятно"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
}

export { AccountsPage };

export default function Accounts() {
  return <AccountsPage {...useOutletContext()} />;
}
